import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Punho de redimensionamento de coluna, encostado na borda direita de um `<th>`.
 *
 * O `<th>` que o recebe precisa ser um elemento posicionado (`relative`,
 * `sticky` ou `absolute`) — senão o punho se ancora no ancestral posicionado
 * mais próximo e aparece no lugar errado.
 */
export function ColumnResizeHandle({
  label,
  onStart,
  onReset,
  active,
  className,
}: {
  /** Nome da coluna, só para leitores de tela. */
  label: string;
  onStart: (e: React.PointerEvent<HTMLElement>) => void;
  /** Duplo clique volta a coluna à largura padrão. */
  onReset?: () => void;
  /** Arraste em curso nesta coluna — mantém a linha destacada. */
  active?: boolean;
  className?: string;
}) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={`Redimensionar coluna ${label}`}
      onPointerDown={(e) => {
        // Sem isto, o arraste também dispara o botão de ordenação que vive
        // dentro do cabeçalho — e a tabela reordena a cada ajuste de largura.
        e.preventDefault();
        e.stopPropagation();
        onStart(e);
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onReset?.();
      }}
      onClick={(e) => e.stopPropagation()}
      className={cn(
        // Inteiramente DENTRO do próprio `<th>`: transbordar para o vizinho
        // colocaria metade da área de clique debaixo do cabeçalho seguinte, que
        // é irmão posicionado e pinta por cima.
        // `touch-none` impede que o gesto seja interpretado como rolagem.
        "group/resize absolute inset-y-0 right-0 z-20 w-2 cursor-col-resize touch-none select-none",
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-y-1.5 right-0 rounded-full transition-colors",
          active
            ? "w-0.5 bg-primary"
            : "w-px bg-border group-hover/resize:w-0.5 group-hover/resize:bg-primary",
        )}
      />
    </div>
  );
}
